<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import leadCaptureStatesAPI from 'dashboard/api/pilot/leadCaptureStates';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const records = ref([]);
const meta = ref({
  currentPage: 1,
  totalEntries: 0,
  perPage: 20,
});
const isLoading = ref(false);
const retryingId = ref(null);
const expandedIds = ref([]);
const showResendDrawer = ref(false);
const selectedRecord = ref(null);
const resendRows = ref([]);

const currentPage = computed(() => Number(route.query.page || 1));

const tableHeaders = computed(() => [
  t('PILOT_LEAD_CAPTURE_HISTORY.TABLE.CONTACT'),
  t('PILOT_LEAD_CAPTURE_HISTORY.TABLE.TARGET'),
  t('PILOT_LEAD_CAPTURE_HISTORY.TABLE.STATUS'),
  t('PILOT_LEAD_CAPTURE_HISTORY.TABLE.LAST_SYNCED_AT'),
  t('PILOT_LEAD_CAPTURE_HISTORY.TABLE.PAYLOAD'),
  t('PILOT_LEAD_CAPTURE_HISTORY.TABLE.ERROR'),
  t('PILOT_LEAD_CAPTURE_HISTORY.TABLE.ACTIONS'),
]);

const formatTarget = target => {
  return t(`PILOT_LEAD_CAPTURE_HISTORY.TARGETS.${target.toUpperCase()}`);
};

const formatStatus = status => {
  return t(`PILOT_LEAD_CAPTURE_HISTORY.STATUSES.${status.toUpperCase()}`);
};

const statusClass = status => {
  return status === 'success'
    ? 'bg-n-amber-3 text-n-ruby-9 dark:bg-n-solid-2 dark:text-n-ruby-11'
    : 'bg-n-teal-3 text-n-slate-12 dark:bg-n-solid-2 dark:text-n-slate-12';
};

const contactTitle = record => {
  const { contact = {} } = record;
  return (
    contact.name ||
    contact.phone_number ||
    contact.email ||
    `#${record.contact_id}`
  );
};

const contactSubtitle = record => {
  const { contact = {} } = record;
  return [contact.phone_number, contact.email, contact.enterprise]
    .filter(Boolean)
    .join(' / ');
};

const payloadSummary = record => {
  const payload = record.payload_snapshot || {};
  const items = payload.items || payload.custom_fields || payload.custom_attributes;
  const source = items && typeof items === 'object' ? items : payload;

  const pairs = Object.entries(source || {})
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .slice(0, 8)
    .map(([key, value]) => `${key}: ${String(value)}`);

  return pairs.length ? pairs.join(' | ') : '-';
};

const CONTACT_STANDARD_FIELDS = ['name', 'email', 'phone_number'];

const editablePayloadItems = record => {
  if (record?.sync_target === 'contact') {
    const payload = record?.payload_snapshot || {};
    const customAttributes =
      payload.custom_attributes && typeof payload.custom_attributes === 'object'
        ? payload.custom_attributes
        : {};
    const standardRows = CONTACT_STANDARD_FIELDS.filter(
      key => payload[key] !== null && payload[key] !== undefined && payload[key] !== ''
    ).map((key, index) => ({
      id: `standard-${key}-${index}`,
      key,
      value: String(payload[key]),
    }));
    const customRows = Object.entries(customAttributes)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value], index) => ({
        id: `custom-${key}-${index}`,
        key,
        value: String(value),
      }));

    return [...standardRows, ...customRows];
  }

  const items = record?.payload_snapshot?.items;
  const source = items && typeof items === 'object' ? items : {};

  return Object.entries(source)
    .filter(([key]) => key !== 'cwContactId')
    .map(([key, value], index) => ({
      id: `${key}-${index}`,
      key,
      value: value == null ? '' : String(value),
    }));
};

const prettyJson = value => JSON.stringify(value || {}, null, 2);

const isExpanded = id => expandedIds.value.includes(id);

const toggleExpanded = id => {
  if (isExpanded(id)) {
    expandedIds.value = expandedIds.value.filter(item => item !== id);
    return;
  }
  expandedIds.value = [...expandedIds.value, id];
};

const fetchRecords = async page => {
  isLoading.value = true;
  try {
    const { data } = await leadCaptureStatesAPI.get({ page });
    records.value = data.records || [];
    meta.value = data.meta || meta.value;
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      error?.message ||
      t('PILOT_LEAD_CAPTURE_HISTORY.API.LIST_ERROR');
    useAlert(message);
  } finally {
    isLoading.value = false;
  }
};

const onPageChange = page => {
  router.push({
    name: 'pilot_lead_capture_history_index',
    query: { page },
  });
};

const retryRecord = async record => {
  retryingId.value = record.id;
  try {
    const payload =
      record.sync_target === 'contact'
        ? resendRows.value.reduce(
            (acc, row) => {
              if (!row.key || row.value === '') {
                return acc;
              }

              if (CONTACT_STANDARD_FIELDS.includes(row.key)) {
                acc[row.key] = row.value;
              } else {
                acc.custom_attributes ||= {};
                acc.custom_attributes[row.key] = row.value;
              }

              return acc;
            },
            {}
          )
        : {
            ...(record.payload_snapshot || {}),
            items: resendRows.value.reduce((acc, row) => {
              if (row.key && row.value !== '') {
                acc[row.key] = row.value;
              }
              return acc;
            }, {}),
          };

    const { data } = await leadCaptureStatesAPI.retry(record.id, payload);
    const nextRecord = data.record;
    records.value = records.value.map(item =>
      item.id === nextRecord.id ? nextRecord : item
    );
    useAlert(data.message || t('PILOT_LEAD_CAPTURE_HISTORY.API.RETRY_SUCCESS'));
    selectedRecord.value = nextRecord;
    resendRows.value = editablePayloadItems(nextRecord);
    showResendDrawer.value = false;
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      error?.message ||
      t('PILOT_LEAD_CAPTURE_HISTORY.API.RETRY_ERROR');
    useAlert(message);
  } finally {
    retryingId.value = null;
  }
};

const openResendDrawer = record => {
  selectedRecord.value = record;
  resendRows.value = editablePayloadItems(record);
  showResendDrawer.value = true;
};

const closeResendDrawer = () => {
  showResendDrawer.value = false;
  selectedRecord.value = null;
  resendRows.value = [];
};

const removeResendRow = rowId => {
  resendRows.value = resendRows.value.filter(row => row.id !== rowId);
};

onMounted(() => {
  fetchRecords(currentPage.value);
});

watch(currentPage, (newPage, oldPage) => {
  if (newPage !== oldPage) {
    fetchRecords(newPage);
  }
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :no-records-found="!isLoading && !records.length"
    :loading-message="$t('PILOT_LEAD_CAPTURE_HISTORY.LOADING')"
    :no-records-message="$t('PILOT_LEAD_CAPTURE_HISTORY.EMPTY_STATE')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('PILOT_LEAD_CAPTURE_HISTORY.TITLE')"
        :description="$t('PILOT_LEAD_CAPTURE_HISTORY.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div class="mt-6 flex-1 text-n-slate-11">
      <woot-loading-state
        v-if="isLoading"
        :message="$t('PILOT_LEAD_CAPTURE_HISTORY.LOADING')"
      />
      <p
        v-else-if="!records.length"
        class="flex h-full flex-col items-center justify-center p-8 text-base"
      >
        {{ $t('PILOT_LEAD_CAPTURE_HISTORY.EMPTY_STATE') }}
      </p>
      <div v-else class="min-w-full overflow-x-auto">
        <table class="min-w-full divide-y divide-n-weak">
          <thead>
            <tr>
              <th
                v-for="header in tableHeaders"
                :key="header"
                class="whitespace-nowrap py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11"
              >
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak text-n-slate-11">
            <template v-for="record in records" :key="record.id">
              <tr>
                <td class="py-4 ltr:pr-4 rtl:pl-4 align-top">
                  <div class="font-medium text-n-slate-12">
                    {{ contactTitle(record) }}
                  </div>
                  <div
                    v-if="contactSubtitle(record)"
                    class="mt-1 text-sm text-n-slate-10"
                  >
                    {{ contactSubtitle(record) }}
                  </div>
                  <div class="mt-1 text-xs text-n-slate-9">
                    ID: {{ record.contact_id }}
                  </div>
                </td>
                <td class="py-4 ltr:pr-4 rtl:pl-4 align-top whitespace-nowrap">
                  {{ formatTarget(record.sync_target) }}
                </td>
                <td class="py-4 ltr:pr-4 rtl:pl-4 align-top whitespace-nowrap">
                  <span
                    class="inline-flex rounded-full px-2 py-1 text-xs font-medium"
                    :class="statusClass(record.latest_status)"
                  >
                    {{ formatStatus(record.latest_status) }}
                  </span>
                </td>
                <td class="py-4 ltr:pr-4 rtl:pl-4 align-top whitespace-nowrap">
                  {{
                    record.last_synced_at
                      ? messageTimestamp(record.last_synced_at, 'MMM dd, yyyy HH:mm')
                      : '-'
                  }}
                </td>
                <td class="max-w-md py-4 ltr:pr-4 rtl:pl-4 align-top">
                  <span class="break-all text-sm">
                    {{ payloadSummary(record) }}
                  </span>
                </td>
                <td class="max-w-sm py-4 ltr:pr-4 rtl:pl-4 align-top">
                  <span class="break-all">
                    {{ record.latest_error_message || '-' }}
                  </span>
                </td>
                <td class="py-4 align-top whitespace-nowrap">
                  <div class="flex items-center gap-2">
                    <Button
                      sm
                      slate
                      faded
                      @click="toggleExpanded(record.id)"
                    >
                      {{
                        isExpanded(record.id)
                          ? $t('PILOT_LEAD_CAPTURE_HISTORY.ACTIONS.HIDE_DETAILS')
                          : $t('PILOT_LEAD_CAPTURE_HISTORY.ACTIONS.VIEW_DETAILS')
                      }}
                    </Button>
                    <Button sm amber faded @click="openResendDrawer(record)">
                      {{ $t('PILOT_LEAD_CAPTURE_HISTORY.ACTIONS.RESEND') }}
                    </Button>
                  </div>
                </td>
              </tr>
              <tr v-if="isExpanded(record.id)" class="bg-n-alpha-1">
                <td colspan="7" class="py-4">
                  <div class="grid gap-4 lg:grid-cols-2">
                    <div class="rounded-lg border border-n-weak bg-n-alpha-2 p-3">
                      <div class="mb-2 text-sm font-medium text-n-slate-12">
                        {{ $t('PILOT_LEAD_CAPTURE_HISTORY.DETAILS.PAYLOAD') }}
                      </div>
                      <pre class="overflow-x-auto whitespace-pre-wrap break-all text-xs">{{ prettyJson(record.payload_snapshot) }}</pre>
                    </div>
                    <div class="rounded-lg border border-n-weak bg-n-alpha-2 p-3">
                      <div class="mb-2 text-sm font-medium text-n-slate-12">
                        {{ $t('PILOT_LEAD_CAPTURE_HISTORY.DETAILS.RESPONSE') }}
                      </div>
                      <pre class="overflow-x-auto whitespace-pre-wrap break-all text-xs">{{ prettyJson(record.response_snapshot) }}</pre>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <TableFooter
          :current-page="Number(meta.currentPage)"
          :total-count="meta.totalEntries"
          :page-size="meta.perPage"
          class="border-n-weak border-t !px-0 py-4"
          @page-change="onPageChange"
        />
      </div>
      </div>
    </template>
  </SettingsLayout>

  <div
    v-if="showResendDrawer"
    class="fixed inset-0 z-50 bg-black/30"
    @click="closeResendDrawer"
  />
  <aside
    v-if="showResendDrawer"
    class="fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl flex-col border-l border-n-weak bg-n-background p-6 shadow-xl"
  >
    <div class="flex items-start justify-between gap-4">
      <div>
        <h3 class="text-xl font-semibold text-n-slate-12">
          {{ $t('PILOT_LEAD_CAPTURE_HISTORY.DRAWER.TITLE') }}
        </h3>
        <p class="mt-1 text-sm text-n-slate-10">
          {{ contactTitle(selectedRecord || {}) }}
        </p>
      </div>
      <Button sm slate faded @click="closeResendDrawer">
        {{ $t('PILOT_LEAD_CAPTURE_HISTORY.DRAWER.CLOSE') }}
      </Button>
    </div>

    <div class="mt-6 overflow-y-auto">
      <div class="rounded-lg border border-n-weak bg-n-alpha-2 p-4">
        <div class="mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('PILOT_LEAD_CAPTURE_HISTORY.DRAWER.LAST_RESPONSE') }}
        </div>
        <pre class="overflow-x-auto whitespace-pre-wrap break-all text-xs">{{ prettyJson(selectedRecord?.response_snapshot) }}</pre>
      </div>

      <div class="mt-6 rounded-lg border border-n-weak bg-n-alpha-2 p-4">
        <div class="mb-4 text-sm font-medium text-n-slate-12">
          {{ $t('PILOT_LEAD_CAPTURE_HISTORY.DRAWER.PAYLOAD') }}
        </div>
        <div class="space-y-3">
          <div
            v-for="row in resendRows"
            :key="row.id"
            class="grid grid-cols-[160px_1fr_auto] items-center gap-3"
          >
            <div class="text-sm font-medium text-n-slate-11">
              {{ row.key }}
            </div>
            <input
              v-model="row.value"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none"
            />
            <Button sm slate faded @click="removeResendRow(row.id)">
              {{ $t('PILOT_LEAD_CAPTURE_HISTORY.DRAWER.DELETE_ROW') }}
            </Button>
          </div>
          <p v-if="!resendRows.length" class="text-sm text-n-slate-10">
            {{ $t('PILOT_LEAD_CAPTURE_HISTORY.DRAWER.EMPTY_PAYLOAD') }}
          </p>
        </div>
      </div>
    </div>

    <div class="mt-6 flex items-center justify-end gap-3 border-t border-n-weak pt-4">
      <Button sm slate faded @click="closeResendDrawer">
        {{ $t('PILOT_LEAD_CAPTURE_HISTORY.DRAWER.CANCEL') }}
      </Button>
      <Button
        sm
        amber
        :is-loading="retryingId === selectedRecord?.id"
        @click="retryRecord(selectedRecord)"
      >
        {{ $t('PILOT_LEAD_CAPTURE_HISTORY.ACTIONS.RESEND') }}
      </Button>
    </div>
  </aside>
</template>
